import { Rect } from 'fabric'
import { useState } from 'react'

export default function useCrop(
  canvas,
  activeObject,
  ignoreHistoryRef,
  pushHistory,
  setDisableUndo,
) {
  const [cropImage, setCropImage] = useState(null)
  const [cropRect, setCropRect] = useState(null)
  const [overlayRect, setOverlayRect] = useState(null)

  // Begin cropping. Draw crop rectangle at the size of the image,
  // and prevent anything else from being selected.
  function enterCropMode() {
    // Disable history for duration of crop mode.
    ignoreHistoryRef.current = true
    canvas.getObjects().forEach((obj) => (obj.selectable = false))

    const img = activeObject
    setCropImage(img)
    setDisableUndo(true)

    const prevCropX = img.cropX || 0
    const prevCropY = img.cropY || 0
    const prevCropWidth = img.width
    const prevCropHeight = img.height

    const imageElement = img.getElement()
    img.set({
      cropX: 0,
      cropY: 0,
      width: imageElement.naturalWidth,
      height: imageElement.naturalHeight,
      left: img.left - prevCropX,
      top: img.top - prevCropY,
    })

    const cropLeft = img.left + prevCropX * (img.scaleX || 1)
    const cropTop = img.top + prevCropY * (img.scaleY || 1)
    const cropWidth = prevCropWidth * (img.scaleX || 1)
    const cropHeight = prevCropHeight * (img.scaleY || 1)

    let cropGeometry = {
      left: cropLeft,
      top: cropTop,
      width: cropWidth * (img.scaleX || 1),
      height: cropHeight * (img.scaleY || 1),
    }

    const crop = new Rect({
      ...cropGeometry,
      fill: 'rgba(0,0,0,0.15)',
      stroke: 'red',
      strokeDashArray: [5, 5],
      cornerColor: 'red',
      transparentCorners: false,
      lockRotation: true,
      selectable: true,
      evented: true,
    })
    crop.setControlsVisibility({ mtr: false })
    setCropRect(crop)

    canvas.add(crop)

    const canvasWidth = canvas.getWidth()
    const canvasHeight = canvas.getHeight()

    const overlay = new Rect({
      left: 0,
      top: 0,
      width: canvasWidth,
      height: canvasHeight,
      fill: 'rgba(0,0,0,0.45)',
      selectable: false,
      evented: false,
      hoverCursor: 'default',
    })
    setOverlayRect(overlay)

    const hole = new Rect({
      left: crop.left,
      top: crop.top,
      width: crop.width * crop.scaleX,
      height: crop.height * crop.scaleY,
      absolutePositioned: true,
      inverted: true, // <--- the key!
    })

    overlay.clipPath = hole

    canvas.add(overlay)
    canvas.bringObjectToFront(crop) // always keep cropRect above overlayRect

    function updateOverlayClip() {
      if (!overlay || !crop) return
      overlay.clipPath.set({
        left: crop.left,
        top: crop.top,
        width: crop.width * crop.scaleX,
        height: crop.height * crop.scaleY,
        absolutePositioned: true,
      })
      canvas.requestRenderAll()
    }

    const onUpdateCrop = function () {
      // Constrain crop to size of image
      const imgLeft = img.left
      const imgTop = img.top
      const imgWidth = img.width * (img.scaleX || 1)
      const imgHeight = img.height * (img.scaleY || 1)

      const left = crop.left,
        top = crop.top,
        width = crop.width * crop.scaleX,
        height = crop.height * crop.scaleY

      const cropWiderThanImg = crop.width * crop.scaleX > imgWidth
      if (cropWiderThanImg) {
        crop.width = imgWidth
        crop.scaleX = 1

        crop.left = imgLeft
        crop.top = imgTop
      } else {
        if (left < imgLeft) {
          crop.left = imgLeft
        }
        if (left + width > imgLeft + imgWidth) {
          crop.left = imgLeft + imgWidth - width
        }
      }

      const cropTallerThanImg = crop.height * crop.scaleY > imgHeight
      if (cropTallerThanImg) {
        crop.height = imgHeight
        crop.scaleY = 1

        crop.left = imgLeft
        crop.top = imgTop
      } else {
        if (top < imgTop) {
          crop.top = imgTop
        }
        if (top + height > imgTop + imgHeight) {
          crop.top = imgTop + imgHeight - height
        }
      }

      crop.setCoords()
      updateOverlayClip()

      canvas.requestRenderAll()
    }

    // Update overlay hole position when cropRect moves/scales
    crop.on('modified', onUpdateCrop)

    updateOverlayClip()
    canvas.setActiveObject(crop)
    canvas.requestRenderAll()
  }

  // Apply mask to image, and re-enable normal canvas functionality.
  async function applyCrop() {
    // Offset of crop relative to image.
    const cropX = (cropRect.left - cropImage.left) / cropImage.scaleX
    const cropY = (cropRect.top - cropImage.top) / cropImage.scaleY

    // Size of crop
    const width = (cropRect.width * cropRect.scaleX) / cropImage.scaleX
    const height = (cropRect.height * cropRect.scaleY) / cropImage.scaleY

    cropImage.set({
      cropX,
      cropY,
      width,
      height,
      top: cropImage.top + cropY * cropImage.scaleY,
      left: cropImage.left + cropX * cropImage.scaleX,
    })
    cropImage.setCoords()

    canvas.remove(overlayRect)
    canvas.remove(cropRect)
    canvas.getObjects().forEach((obj) => (obj.selectable = true))
    canvas.renderAll()

    ignoreHistoryRef.current = false
    pushHistory(canvas)

    setOverlayRect(null)
    setCropRect(null)
    setCropImage(null)
    setDisableUndo(false)
  }

  return { cropImage, enterCropMode, applyCrop }
}
